import { ITelecomFeePlan } from '../../../../../../shared/constants/telecomFeePlans';
import type {
  ICalculatorFormValues,
  ICalculatorMode,
  ICalculatorResultItem,
  ICalculatorResultKey,
  ICalculatorSummary,
} from '../../../../../../shared/types/device';
import { calculateDataUsage } from '../../../../../../shared/utils/calcDataUsage';

export const defaultCalculatorMode: ICalculatorMode = 'mfm';
export const calculatorModeOptions = [
  { label: 'MFM 1.4.0 이상', value: 'mfm' },
  { label: '커스텀/이전 버전', value: 'custom' },
];

export const calculatorItems: ICalculatorResultItem[] = [
  {
    key: 'serverConnection',
    resultKey: 'serverConnectionUsage',
    label: '서버 연결',
    unit: '회',
    helperText: '1달 동안 (재)연결 시도 횟수',
    calculate: (values) => calculateDataUsage.serverConnection(values.serverConnection),
  },
  {
    key: 'heartbeatCycle',
    resultKey: 'heartbeatUsage',
    label: 'Heartbeat 주기',
    unit: '초',
    helperText: '클라우드 서버와의 연결 상태 확인',
    calculate: (values) => {
      return values.heartbeatCycle > 0 ? calculateDataUsage.heartbeat(values.heartbeatCycle) : 0;
    },
  },
  {
    key: 'dataCollectionCount',
    resultKey: 'dataCollectionUsage',
    label: '데이터 수집 개수',
    unit: '개',
    helperText: '주기적으로 전송할 데이터의 개수',
    calculate: (values, mode) => {
      if (values.dataTransferCycle <= 0 || values.dataCollectionCount <= 0) {
        return 0;
      }

      return calculateDataUsage.dataCollection(mode, values.dataTransferCycle, values.dataCollectionCount);
    },
  },
  {
    key: 'arrayDataCollectionCount',
    resultKey: 'arrayDataCollectionUsage',
    label: '배열 개수',
    unit: '개',
    helperText: '전송할 배열 데이터의 개수',
  },
  {
    key: 'arrayElementCount',
    resultKey: 'arrayElementUsage',
    label: '배열 원소 개수',
    unit: '개',
    helperText: '원소 개수의 총합',
    calculate: (values, mode) => {
      if (
        mode !== 'mfm' ||
        values.dataTransferCycle <= 0 ||
        values.arrayDataCollectionCount <= 0 ||
        values.arrayElementCount <= 0
      ) {
        return 0;
      }

      return calculateDataUsage.arrayElement(values.arrayElementCount, values.arrayDataCollectionCount, values.dataTransferCycle);
    },
  },
  {
    key: 'dataTransferCycle',
    resultKey: 'dataTransferUsage',
    label: '데이터 전송 주기',
    unit: '초',
    helperText: '클라우드로 데이터를 전송할 주기',
  },
  {
    key: 'alarmCount',
    resultKey: 'alarmUsage',
    label: '알람 발생 횟수',
    unit: '개',
    helperText: '1달 동안 발생할 알람 이벤트의 개수',
    calculate: (values) => calculateDataUsage.alarm(values.alarmCount),
  },
];

export const calculatorResultKeys: ICalculatorResultKey[] = calculatorItems.map((item) => item.resultKey);

const createEmptyUsageResults = (): Record<ICalculatorResultKey, number> => ({
  serverConnectionUsage: 0,
  heartbeatUsage: 0,
  dataCollectionUsage: 0,
  arrayDataCollectionUsage: 0,
  arrayElementUsage: 0,
  dataTransferUsage: 0,
  alarmUsage: 0,
});

export const getUsageResults = (values: ICalculatorFormValues, mode: ICalculatorMode): Record<ICalculatorResultKey, number> => {
  return calculatorItems.reduce<Record<ICalculatorResultKey, number>>((acc, item) => {
    const usageBytes = item.calculate ? item.calculate(values, mode) : 0;

    acc[item.resultKey] = usageBytes > 0 ? Math.round(usageBytes) : 0;

    return acc;
  }, createEmptyUsageResults());
};

/** 데이터 사용량 합산 후 MB 단위로 변환 */
export const getTotalUsageMb = (results: Record<ICalculatorResultKey, number>, mode: ICalculatorMode) => {
  const targetKeys =
    mode === 'custom'
      ? calculatorResultKeys.filter((key) => key !== 'arrayDataCollectionUsage' && key !== 'arrayElementUsage')
      : calculatorResultKeys;

  const totalBytes = targetKeys.reduce((sum, key) => sum + results[key], 0);
  const totalUsageMb = Number((totalBytes / (1024 * 1024)).toFixed(2));

  return totalUsageMb + 2 * 12.15;
};

/** 데이터 사용 추정량 */
export const estimateDataUsage = (totalUsageMb: number) => {
  return totalUsageMb * 1.1; // 10% 여유분 추가
};

/** 초과요금 계산 */
export const calculateOverageUsage = (estimatedUsage: number, basicData: number) => {
  return basicData < estimatedUsage ? -(basicData - estimatedUsage) : 0;
};
export const getCalculatorSummary = (
  values: ICalculatorFormValues,
  mode: ICalculatorMode,
  selectedPlanPricing: ITelecomFeePlan,
): ICalculatorSummary => {
  const customUsageResults = getUsageResults(values, 'custom');
  const mfmUsageResults = getUsageResults(values, 'mfm');
  const activeUsageResults = mode === 'mfm' ? mfmUsageResults : customUsageResults;
  const customTotalUsageMb = getTotalUsageMb(customUsageResults, 'custom');
  const mfmTotalUsageMb = getTotalUsageMb(mfmUsageResults, 'mfm');
  const activeTotalUsageMb = mode === 'mfm' ? estimateDataUsage(mfmTotalUsageMb) : estimateDataUsage(customTotalUsageMb);

  const overageUsageMb = calculateOverageUsage(activeTotalUsageMb, selectedPlanPricing.basicData);
  const overageCostPerMb = Math.round(selectedPlanPricing.extraFeePer1mb);
  const overageCost = overageUsageMb * overageCostPerMb;

  return {
    selectedPlanPricing,
    activeUsageResults,
    estimatedCostItems: [
      {
        label: '예상 요금',
        value: selectedPlanPricing.basicFare + overageCost,
        prefix: '₩ ',
        fractionDigits: 2,
      },
    ],
    costBreakdownItems: [
      {
        label: '데이터 사용 추정량',
        value: activeTotalUsageMb,
        suffix: ' MB',
        fractionDigits: 2,
      },
      {
        label: '기본 사용량',
        value: selectedPlanPricing.basicData,
        suffix: ' MB',
      },
      {
        label: '기본 요금(원)',
        value: selectedPlanPricing.basicFare,
        prefix: '₩ ',
      },
      {
        label: '초과 사용량',
        value: overageUsageMb,
        suffix: ' MB',
        fractionDigits: 2,
      },
      {
        label: '1MB 초과 당 요금',
        value: overageCostPerMb,
        prefix: '₩ ',
      },
      {
        label: '초과 요금',
        value: Math.round(overageCost),
        prefix: '₩ ',
      },
    ],
  };
};
